import { ref, onMounted, onBeforeUnmount, watch } from 'vue'

/**
 * Composable for intelligent sticky behavior
 * Element becomes sticky only when it scrolls out of view
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.offset - Offset from top when stuck (default: 0)
 * @param {boolean} options.disabled - Disable sticky behavior (default: false)
 * @returns {Object} - { elementRef, isStuck, disable, enable }
 */
export function useSticky(options = {}) {
  const { 
    offset = 0
  } = options

  const elementRef = ref(null)
  const isStuck = ref(false)
  const isDisabled = ref(false)
  let scrollHandler = null

  const checkStickyState = () => {
    if (!elementRef.value || isDisabled.value) {
      isStuck.value = false
      return
    }

    // Get element's position relative to viewport
    const rect = elementRef.value.getBoundingClientRect()
    
    // Element is out of view (scrolled past) if its top is above viewport
    const isOutOfView = rect.top <= offset
    
    isStuck.value = isOutOfView
  }

  const setupSticky = () => {
    if (!elementRef.value) return

    // Check initial state
    checkStickyState()

    // Add scroll listener for real-time updates
    scrollHandler = () => {
      checkStickyState()
    }

    window.addEventListener('scroll', scrollHandler, { passive: true })
  }

  const cleanup = () => {
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler)
      scrollHandler = null
    }
  }
  
  // Temporarily disable sticky behavior
  const disable = () => {
    isDisabled.value = true
    isStuck.value = false
  }
  
  // Re-enable sticky behavior and check state
  const enable = () => {
    isDisabled.value = false
    // Check state after a brief delay to allow DOM to settle
    setTimeout(checkStickyState, 100)
  }

  // Watch for element changes
  watch(elementRef, (newVal) => {
    if (newVal) {
      setupSticky()
    }
  })

  onMounted(() => {
    // Delay setup to ensure DOM is ready
    setTimeout(setupSticky, 100)
  })

  onBeforeUnmount(() => {
    cleanup()
  })

  return {
    elementRef,
    isStuck,
    disable,
    enable
  }
}

